import React, { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  useListConversations,
  ConversationListItem,
} from '../../api/conversations';
import { EyeIcon } from '@heroicons/react/24/outline';
import RefreshButton from '../../components/common/RefreshButton';
import DateWithTooltip from '../../components/common/DateWithTooltip';
import DurationWithTooltip from '../../components/common/DurationWithTooltip';
import { EmptyState } from '../../components/common/EmptyState';
import { LoadingState } from '../../components/common/LoadingState';
import { ConversationStatusBadge } from '../../components/common/ConversationStatusBadge';

import { useTranslation } from 'react-i18next';

interface TaskExecutionHistoryProps {
  scope: string;
  taskId: string;
  onViewExecution?: (conversationId: string) => void;
}

export const TaskExecutionHistory: React.FC<TaskExecutionHistoryProps> = ({
  taskId,
  onViewExecution,
}) => {
  const { t } = useTranslation('pleri/pages');
  const navigate = useNavigate();
  const { data, isLoading, isFetching, refetch } = useListConversations({
    source: 'task',
    sourceId: taskId,
    limit: 50,
  });

  const handleViewExecution = (conversation: ConversationListItem) => {
    if (onViewExecution) {
      onViewExecution(conversation.conversationId);
    } else {
      // Navigate to conversation view
      navigate({
        to: '/c/$conversationId',
        params: { conversationId: conversation.conversationId },
      });
    }
  };

  // Sort conversations by createdAt in descending order (latest first)
  const sortedConversations = useMemo(() => {
    if (!data?.conversations) {
      return [];
    }
    return [...data.conversations].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Descending order (newest first)
    });
  }, [data?.conversations]);

  if (isLoading) {
    return <LoadingState text={t('loadingExecutionHistory')} />;
  }
  if (!data || !data.conversations || data.conversations.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyState
          title={t('noExecutionsYet')}
          description={t('runThisTaskToSeeExecutionHistory')}
        />
        <div className="flex justify-center">
          <RefreshButton
            onClick={() => refetch()}
            aria-label={t('refreshHistory')}
            title={t('refreshHistory')}
            loading={isFetching}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('executionHistory')}
        </h3>
        <RefreshButton
          onClick={() => refetch()}
          aria-label={t('refreshHistory')}
          title={t('refreshHistory')}
          loading={isFetching}
        />
      </div>
      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                {t('common:status')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                {t('started')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                {t('duration')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                {t('messages')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-gray-400">
                {t('actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
            {sortedConversations.map((conversation: ConversationListItem) => (
              <tr
                key={conversation.conversationId}
                className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <ConversationStatusBadge status={conversation.status} />
                </td>
                <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-900 dark:text-gray-100">
                  <DateWithTooltip date={conversation.createdAt} />
                </td>
                <td className="px-4 py-3 text-sm whitespace-nowrap text-gray-900 dark:text-gray-100">
                  {conversation.updatedAt &&
                  conversation.createdAt !== conversation.updatedAt ? (
                    <DurationWithTooltip
                      start={conversation.createdAt}
                      end={conversation.updatedAt}
                    />
                  ) : (
                    <span className="text-gray-400 dark:text-gray-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                  <span className="text-gray-600 dark:text-gray-400">
                    {t('messageCount', { count: conversation.messageCount })}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button
                    onClick={() => handleViewExecution(conversation)}
                    className="rounded-lg p-2 text-gray-400 transition-all duration-150 hover:bg-orange-50 hover:text-orange-600 dark:text-gray-500"
                    aria-label={t('viewExecutionDetails')}
                    title={t('viewExecutionDetails')}
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
